import { AutoLoadContext, FetchCallback } from '@/components/organisms/auto-load/model'
import { ListWithPagination, Pagination } from '@/models/api/response/list'
import ProductclassService from '@/services/api/productclass'
import { Either, right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import { withoutPagingQuery } from '../query'

/**
 * オートロードの実行・継続判断は {@link AutoLoadContainer}が判断しているが
 * {@link AutoLoadContainer}は具体的なデータ取得方法については関知しておらず、
 * データ取得結果に基づいて構築されるページ状態にのみ基づいて上記判断を行っている
 *
 * データ取得〜ページ状態構築は互いに待ち合わせず個別に非同期で行われるため、
 * 「取得すべきデータはもう残っていないが、オートロード処理自体は開始される」ということが有り得る
 *
 * 例えば以下ケース:
 * - 表示すべき総商品数は33点、1ページあたりの表示数は30点
 * - 30点はサーバー側で事前にレンダリングされ、2ページ目以降はclientでオートロードされる
 * - 2ページ目のオートロード中、スクロールの影響で3ページ目のオートロードも発火
 *   - 3ページ目のオートロード処理は2ページ目の結果を待たず非同期に発火している
 *   - 2ページ目の結果が終わっていない = 2ページ目で終わりかどうかページング状態からはまだわかっていない
 *   - 3ページ目のオートロード処理はページング状態に基づいて抑制されず、実行される
 *
 * ページ途中で再読み込みしたケースなど、複数ページを可能な限り高速に取得・表示する必要があるケースもあるため、
 * 各ページの取得結果を待つ（＝先頭から1ページずつ順に読み込む）仕組みは許容できない
 *
 * よって、個別のデータ取得結果にもとづくオートロードの要不要は
 * 呼び出し側である{@link AutoLoadContainer}では判断不可能なため、
 * 個別のデータ取得手順側でフォローする必要がある
 */
export interface AutoLoadStrategy<T> {
  readonly fetchPage: FetchCallback<T>
}

export class AutoLoadWithQuery<T> implements AutoLoadStrategy<T> {
  constructor(private readonly params: Readonly<{
    productclassService: ProductclassService,
  }>) {
  }

  fetchPage(baseQuery: object): Promise<Either<Error, ListWithPagination<T>>> {
    return this.params.productclassService.listFkuWithPagination(baseQuery)
  }
}

export class AutoLoadByFkuIds<T> implements AutoLoadStrategy<T> {
  public readonly chunkedIdLists: ReadonlyArray<ReadonlyArray<string>>

  constructor(private readonly params: Readonly<{
    ids: {
      all: string[]
      loadedOnServer: string[],
      loadOnClient: string[]
    },
    productclassService: ProductclassService,
    countPerPage: number
  }>) {
    // chunkの結果はコンストラクタで渡されたオブジェクトによって一定なので、
    // コンストラクタで事前に算出しておく
    this.chunkedIdLists = _.chunk(this.params.ids.loadOnClient, this.params.countPerPage)
  }

  async fetchPage(baseQuery: object, context: AutoLoadContext): Promise<Either<Error, ListWithPagination<T>>> {
    if (this.params.ids.loadOnClient.length === 0) {
      return Promise.resolve(
        right(
          new ListWithPagination({
            list: [],
            pagination: new Pagination({
              total: this.totalOfPagination,
              length: this.totalOfPagination,
              remainder: 0,
            }),
          })
        )
      )
    }
    const index = Math.max(
      context.targetPage - context.basePage,
      0
    )

    const idList = this.chunkedIdLists[index] || null
    if (idList === null) {
      /**
       * オートロードの非同期実行により、chunkの範囲を超えてアクセスされる可能性がある
       * @see AutoLoadStrategy
       * @see https://github.com/Everforth/growthpalette-front/issues/8062
       */
      return right(new ListWithPagination({
        list: [],
        pagination: new Pagination({
          total: this.totalOfPagination,
          length: 0,
          remainder: 0,
        }),
      }))
    }

    return this.params.productclassService.listFkuWithPagination({
      /**
       * {@link AutoLoadContainer}でlimit/startがローディング状態に応じて注入されるが、
       * ids指定の場合は余計である（むしろこれによって、指定されたidの商品が取得できない場合がある）ため、
       * paging関係のクエリは除外して利用
       *
       * 次のloadで想定するロード範囲を注入する動作自体は auto-load の処理として不自然でもないため、
       * ids用の処理側で対処している.
       */
      ...withoutPagingQuery(baseQuery),
      ids: idList.join(','),
    }).then(result => result.map(v => new ListWithPagination({
      /**
       * リクエストのid順に返却されるわけではないのでソートし直す
       * @see https://github.com/Everforth/growthpalette-front/issues/8014
       */
      list: v.list.sort((a, b) => idList.indexOf(a.id) - idList.indexOf(b.id)),
      pagination: v.pagination.with({
        total: this.totalOfPagination,
        remainder: Math.max(this.totalOfPagination - idList.length * (index + 1), 0),
      }),
    })))
  }

  /**
   * ID指定で取得する場合の、該当するデータの**予定**総数
   *
   * 一定の性質を満たすもののクエリだと、その一回のクエリ実行で取れる件数とその性質を満たすtotalの件数が食い違うのが普通.
   *
   * 一方、ID指定のクエリでは、その一回のクエリ実行で取れる件数とtotalの件数は同じものとしてEngineが返してくる
   * （意味的にもほかに"total"を取りようがない）
   *
   * しかしID指定によるオートロードの場合、オートロード続行の判断基準となる「総数」はリクエストに指定したIDの件数とは一致せず、
   * リクエストの範囲外で用意されている「IDの全体」によって定められる必要がある.
   *
   * つまり、性質を指定するクエリと異なり、ID指定のクエリにおいてはEngineの返すtotalをそのまま利用することはできない.
   *
   * そのため、ID指定のオートロードではEngineの算出したtotalを参照するのではなく、
   * 別途保持している指定IDの全体を基準に「total」を導出する必要がある.
   *
   * 「**予定**総数」としているのは、ID指定されているデータが実際には非公開で取得できない可能性があり、
   * しかしそのことをオートロードより事前に把握できないため.
   *
   * @see https://github.com/Everforth/growthpalette-front/pull/8175#pullrequestreview-2358254121
   */
  private get totalOfPagination(): number {
    return this.params.ids.all.length
  }
}
